import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Autocomplete,
  CircularProgress,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import LeaveRequestService, { LeaveRequest } from '../../services/leave-request.service';
import EmployeeService from '../../services/employee.service';

const LeaveRequestForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);

  const [formData, setFormData] = useState<LeaveRequest>({
    employee: 0,
    leaveType: 'vacation',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    reason: '',
    status: 'pending',
    totalDays: 1,
  });

  useEffect(() => {
    fetchEmployees();
    if (isEdit && id) {
      fetchLeaveRequest(parseInt(id));
    }
  }, [id, isEdit]);

  // Auto-calculate total days when dates change
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const days = calculateTotalDays(formData.startDate, formData.endDate);
      setFormData((prev) => ({ ...prev, totalDays: days }));
    }
  }, [formData.startDate, formData.endDate]);

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const response = await EmployeeService.getAll({
        'pagination[pageSize]': 100,
        'filters[status][$eq]': 'active',
      });
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchLeaveRequest = async (leaveRequestId: number) => {
    try {
      setLoading(true);
      const response = await LeaveRequestService.getOne(leaveRequestId);
      const data = response.data?.data;

      if (data) {
        const attrs = data.attributes;
        setFormData({
          employee: attrs.employee?.data?.id || 0,
          leaveType: attrs.leaveType || 'vacation',
          startDate: attrs.startDate,
          endDate: attrs.endDate,
          reason: attrs.reason || '',
          status: attrs.status || 'pending',
          totalDays: attrs.totalDays || 1,
        });

        // Set selected employee for autocomplete
        if (attrs.employee?.data) {
          setSelectedEmployee(attrs.employee.data);
        }
      }
    } catch (error: any) {
      console.error('Error fetching leave request:', error);
      setError('Failed to load leave request');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both days

    return diffDays > 0 ? diffDays : 1;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEmployeeChange = (event: any, newValue: any) => {
    setSelectedEmployee(newValue);
    setFormData({ ...formData, employee: newValue?.id || 0 });
  };

  const validateForm = (): boolean => {
    if (!formData.employee) {
      setError('Please select an employee');
      return false;
    }
    if (!formData.startDate) {
      setError('Please select a start date');
      return false;
    }
    if (!formData.endDate) {
      setError('Please select an end date');
      return false;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setError('End date must be after start date');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const submitData = {
        ...formData,
        totalDays: calculateTotalDays(formData.startDate, formData.endDate),
      };

      if (isEdit && id) {
        await LeaveRequestService.update(parseInt(id), submitData);
        setSuccess('Leave request updated successfully!');
      } else {
        await LeaveRequestService.create(submitData);
        setSuccess('Leave request created successfully!');
      }

      setTimeout(() => {
        navigate('/leave-requests');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving leave request:', error);
      setError(
        error.response?.data?.error?.message ||
          'Failed to save leave request. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const getLeaveTypeDescription = (type: string) => {
    const descriptions: { [key: string]: string } = {
      vacation: 'Annual vacation leave',
      sick: 'Medical leave for illness',
      personal: 'Personal time off',
      unpaid: 'Leave without pay',
      maternity: 'Maternity leave for new mothers',
      paternity: 'Paternity leave for new fathers',
    };
    return descriptions[type] || '';
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {isEdit ? 'Edit Leave Request' : 'New Leave Request'}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Main Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Leave Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {/* Employee Selection */}
              <Autocomplete
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                options={employees}
                loading={employeesLoading}
                getOptionLabel={(option) =>
                  `${option.attributes?.firstName} ${option.attributes?.lastName} (${option.attributes?.employeeNumber})`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee *"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {employeesLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Leave Type */}
              <TextField
                select
                fullWidth
                label="Leave Type"
                name="leaveType"
                value={formData.leaveType}
                onChange={handleChange}
                required
                helperText={getLeaveTypeDescription(formData.leaveType)}
              >
                <MenuItem value="vacation">🏖️ Vacation</MenuItem>
                <MenuItem value="sick">🤒 Sick Leave</MenuItem>
                <MenuItem value="personal">👤 Personal Leave</MenuItem>
                <MenuItem value="unpaid">💰 Unpaid Leave</MenuItem>
                <MenuItem value="maternity">👶 Maternity Leave</MenuItem>
                <MenuItem value="paternity">👨‍👧 Paternity Leave</MenuItem>
              </TextField>

              {/* Date Range */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />

                <TextField
                  fullWidth
                  label="End Date"
                  name="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Stack>

              {/* Total Days Display */}
              <Card variant="outlined" sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1">Total Leave Days</Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formData.totalDays} {formData.totalDays === 1 ? 'day' : 'days'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>

              {/* Reason */}
              <TextField
                fullWidth
                label="Reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="Please provide a reason for your leave request..."
                helperText="Provide details about why you need this leave"
              />

              {/* Status (only show in edit mode) */}
              {isEdit && (
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
              )}
            </Stack>
          </Paper>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/leave-requests')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Request' : 'Submit Request'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default LeaveRequestForm;

